import React, { useCallback, useEffect, useRef, useState } from 'react';

const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SCRIPT_ID = 'cf-turnstile-api';

type TurnstileRenderOptions = {
  sitekey: string;
  action?: string;
  theme?: 'light' | 'dark' | 'auto';
  callback?: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: () => void;
};

type TurnstileApi = {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId?: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptLoadPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Turnstile requires a browser'));
  }
  if (window.turnstile) {
    return Promise.resolve();
  }
  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  scriptLoadPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Turnstile script failed')), {
        once: true,
      });
      if (window.turnstile) {
        resolve();
      }
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptLoadPromise = null;
      reject(new Error('Turnstile script failed to load'));
    };
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

export interface TurnstileWidgetProps {
  siteKey: string;
  onToken: (token: string | null) => void;
  /** Increment / change to force a widget reset (e.g. after each submit). */
  resetSignal?: number;
  className?: string;
}

/**
 * Cloudflare Turnstile (explicit render, action=login).
 * Charge le script officiel une seule fois ; nettoie le widget au démontage.
 */
const TurnstileWidget: React.FC<TurnstileWidgetProps> = ({
  siteKey,
  onToken,
  resetSignal = 0,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onTokenRef = useRef(onToken);
  const [loadError, setLoadError] = useState<string | null>(null);
  onTokenRef.current = onToken;

  const clearToken = useCallback(() => {
    onTokenRef.current(null);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const mount = async () => {
      if (!siteKey || !containerRef.current) {
        return;
      }
      setLoadError(null);
      try {
        await loadTurnstileScript();
      } catch {
        if (!cancelled) {
          onTokenRef.current(null);
          setLoadError(
            'Impossible de charger le script Cloudflare Turnstile. Vérifiez votre réseau / bloqueur de pubs.'
          );
        }
        return;
      }
      if (cancelled || !containerRef.current || !window.turnstile) {
        return;
      }

      if (widgetIdRef.current) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* ignore */
        }
        widgetIdRef.current = null;
        containerRef.current.innerHTML = '';
      }

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        action: 'login',
        theme: 'light',
        callback: (token: string) => {
          setLoadError(null);
          onTokenRef.current(token);
        },
        'expired-callback': () => {
          clearToken();
        },
        'error-callback': () => {
          clearToken();
          setLoadError(
            'Cloudflare n’a pas pu afficher le captcha (hostname non autorisé ?). ' +
              'En local : redémarrez le front après avoir mis la Site Key de test ' +
              '1x0000000000000000000000000000000AA, ou ajoutez localhost dans le widget Cloudflare.'
          );
        },
      });
    };

    mount();

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* ignore */
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, clearToken]);

  useEffect(() => {
    if (!resetSignal || !widgetIdRef.current || !window.turnstile) {
      return;
    }
    try {
      window.turnstile.reset(widgetIdRef.current);
    } catch {
      /* ignore */
    }
    clearToken();
  }, [resetSignal, clearToken]);

  if (!siteKey) {
    return (
      <div className={className} data-testid="turnstile-widget-missing-key">
        <p className="text-danger small mb-0">
          Turnstile site key manquante (REACT_APP_TURNSTILE_SITE_KEY).
        </p>
      </div>
    );
  }

  return (
    <div className={className} data-testid="turnstile-widget">
      <div ref={containerRef} />
      {loadError && (
        <p className="text-danger small mt-2 mb-0" role="alert">
          {loadError}
        </p>
      )}
    </div>
  );
};

export default TurnstileWidget;
